import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { API } from '../context/AuthContext';

const periods = [
  { label: '1W', value: 7 },
  { label: '1M', value: 30 },
  { label: '1Y', value: 365 }
];

const COIN_IDS = {
  "XRP": "ripple", "BTC": "bitcoin", "ETH": "ethereum", "SOL": "solana",
  "ADA": "cardano", "HBAR": "hedera-hashgraph", "XLM": "stellar",
  "LTC": "litecoin", "DOT": "polkadot", "LINK": "chainlink",
  "TRX": "tron", "DOGE": "dogecoin", "MATIC": "matic-network",
  "ALGO": "algorand", "ATOM": "cosmos", "HYPE": "hyperliquid",
  "WBT": "whitebit", "USDT": "tether", "USDC": "usd-coin",
  "QNT": "quant-network", "VET": "vechain", "ICP": "internet-computer",
  "FIL": "filecoin", "SAND": "the-sandbox", "MANA": "decentraland",
  "AVAX": "avalanche-2", "NEAR": "near", "FTM": "fantom",
  "THETA": "theta-token", "GRT": "the-graph", "ENJ": "enjincoin",
  "CHZ": "chiliz", "BAT": "basic-attention-token", "ZEC": "zcash",
  "DASH": "dash", "XMR": "monero", "ETC": "ethereum-classic",
  "BCH": "bitcoin-cash", "BNB": "binancecoin", "SHIB": "shiba-inu",
  "UNI": "uniswap", "AAVE": "aave", "MKR": "maker", "SNX": "havven",
  "CRV": "curve-dao-token", "COMP": "compound-governance-token",
  "YFI": "yearn-finance", "SUSHI": "sushi", "1INCH": "1inch",
  "RUNE": "thorchain", "EGLD": "elrond-erd-2", "ONE": "harmony",
  "HOT": "holotoken", "ZIL": "zilliqa", "ICX": "icon",
  "ONT": "ontology", "IOTA": "iota", "NEO": "neo",
  "EOS": "eos", "XTZ": "tezos", "WAVES": "waves",
  "KSM": "kusama", "FLOW": "flow", "MINA": "mina-protocol",
  "ROSE": "oasis-network", "AR": "arweave", "KDA": "kadena",
  "CELO": "celo", "STX": "blockstack", "HNT": "helium",
  "AXS": "axie-infinity", "GALA": "gala", "IMX": "immutable-x",
  "LRC": "loopring", "OMG": "omisego", "ZRX": "0x",
  "STORJ": "storj", "SKL": "skale", "NMR": "numeraire",
  "OGN": "origin-protocol", "ANKR": "ankr", "CELR": "celer-network",
  "BAND": "band-protocol", "REN": "republic-protocol",
  "CSPR": "casper-network", "PEPE": "pepe", "WIF": "dogwifcoin", "SUI": "sui", "APT": "aptos", "INJ": "injective-protocol", "SEI": "sei-network", "TIA": "celestia", "BONK": "bonk",
  "OXT": "orchid-protocol", "KEEP": "keep-network",
  "NU": "nucypher", "FET": "fetch-ai", "OCEAN": "ocean-protocol",
  "AGIX": "singularitynet", "NKN": "nkn", "DENT": "dent",
  "WIN": "wink", "BTT": "bittorrent-2", "JST": "just",
  "SUN": "sun-token", "LUNC": "terra-luna", "LUNA": "terra-luna-2",
  "UST": "terrausd", "MIR": "mirror-protocol"
};

const fmt = (n) => '$' + Math.round(n || 0).toLocaleString();
const fmtSmall = (n) => {
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'k';
  return '$' + parseFloat(n || 0).toFixed(0);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ color: '#6b6b6b', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{fmt(payload[0].value)}</div>
      </div>
    );
  }
  return null;
};

export default function PortfolioChart({ currentValue, holdings }) {
  const [snapshots, setSnapshots] = useState([]);
  const [period, setPeriod] = useState(7);
  const [selectedCoin, setSelectedCoin] = useState('ALL');
  const [loading, setLoading] = useState(true);

  const coins = holdings ? [...new Set(holdings.map(h => h.coin_symbol))] : [];

  useEffect(() => {
    if (selectedCoin === 'ALL') {
      loadPortfolioSnapshots();
    } else {
      loadCoinHistory();
    }
  }, [period, selectedCoin, holdings]);

  const loadPortfolioSnapshots = async () => {
    setLoading(true);
    try {
      const periodMap = { 7: 'week', 30: 'month', 365: 'year' };
      const res = await axios.get(`${API}/api/prices/snapshots?period=${periodMap[period]}`);
      const data = res.data.snapshots.map(s => ({
        date: new Date(s.snapshot_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(s.total_value)
      }));
      if (data.length === 1) {
        const costBasis = holdings?.reduce((sum, h) => sum + parseFloat(h.amount) * parseFloat(h.average_buy_price), 0) || 0;
        data.unshift({ date: 'Start', value: costBasis });
      }
      if (data.length === 0 && currentValue) {
        data.push({ date: 'Today', value: currentValue });
      }
      setSnapshots(data);
    } catch (err) {
      console.error('Chart load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCoinHistory = async () => {
    setLoading(true);
    try {
      const coinHoldings = holdings?.filter(h => h.coin_symbol === selectedCoin) || [];
      const totalAmount = coinHoldings.reduce((sum, h) => sum + parseFloat(h.amount), 0);
      const totalCost = coinHoldings.reduce((sum, h) => sum + parseFloat(h.average_buy_price) * parseFloat(h.amount), 0);
      const avgBuyPrice = totalAmount > 0 ? totalCost / totalAmount : 0;

      if (totalAmount === 0) { setSnapshots([]); setLoading(false); return; }
      const coinId = selectedCoin;
        const res = await axios.get(`${API}/api/prices/history/${coinId}?days=${period}`);
      const prices = res.data.prices;
      const cutoff = Date.now() - (period * 24 * 60 * 60 * 1000);
      const filtered = prices.filter(([ts]) => ts >= cutoff);
      const byDay = {};
      filtered.forEach(([timestamp, price]) => {
        const dateKey = new Date(timestamp).toLocaleDateString("en-US", period === 365 ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" });
        byDay[dateKey] = price * totalAmount;
      });
      const data = Object.entries(byDay).map(([date, value]) => ({ date, value }));
      const costBasisLine = avgBuyPrice * totalAmount;
      setSnapshots({ data, costBasis: costBasisLine, totalAmount, avgBuyPrice });

    } catch (err) {
      console.error('Coin history error:', err);
      setSnapshots([]);
    } finally {
      setLoading(false);
    }
  };

  const chartData = Array.isArray(snapshots) ? snapshots : snapshots.data || [];
  const costBasis = Array.isArray(snapshots) ? null : snapshots.costBasis;

  const minValue = chartData.length > 0 ? Math.min(...chartData.map(s => s.value)) * 0.95 : 0;
  const maxValue = chartData.length > 0 ? Math.max(...chartData.map(s => s.value)) * 1.05 : (currentValue || 100) * 1.1;
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || currentValue;
  const change = lastValue - firstValue;
  const changePct = firstValue > 0 ? (change / firstValue) * 100 : 0;
  const isPositive = change >= 0;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <div style={s.chartTitle}>
            {selectedCoin === 'ALL' ? 'Portfolio performance' : `${selectedCoin} performance`}
          </div>
          {chartData.length > 1 && (
            <div style={{ fontSize: '13px', color: isPositive ? '#1a6b4a' : '#dc2626', marginTop: '2px' }}>
              {isPositive ? '+' : ''}{fmt(change)} ({isPositive ? '+' : ''}{changePct.toFixed(1)}%) this period
            </div>
          )}
          {selectedCoin !== 'ALL' && costBasis && (
            <div style={{ fontSize: '12px', color: '#6b6b6b', marginTop: '2px' }}>
              Cost basis: {fmt(costBasis)}
            </div>
          )}
        </div>
        <div style={s.periodBtns}>
          {periods.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              style={{ ...s.periodBtn, ...(period === p.value ? s.periodBtnActive : {}) }}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {coins.length > 0 && (
        <div style={s.coinPills}>
          <button onClick={() => setSelectedCoin('ALL')}
            style={{ ...s.pill, ...(selectedCoin === 'ALL' ? s.pillActive : {}) }}>
            All
          </button>
          {coins.map(coin => (
            <button key={coin} onClick={() => setSelectedCoin(coin)}
              style={{ ...s.pill, ...(selectedCoin === coin ? s.pillActive : {}) }}>
              {coin}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading chart...</div>
      ) : chartData.length === 0 ? (
        <div style={s.empty}>Historical data is being loaded for this coin. Check back in a few hours — your portfolio value and gains are still accurate above.</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={isPositive ? '#1a6b4a' : '#dc2626'} stopOpacity={0.15}/>
                <stop offset="95%" stopColor={isPositive ? '#1a6b4a' : '#dc2626'} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9b9b9b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis domain={[minValue, maxValue]} tick={{ fontSize: 10, fill: '#9b9b9b' }} axisLine={false} tickLine={false} tickFormatter={fmtSmall} width={45} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="value" stroke={isPositive ? '#1a6b4a' : '#dc2626'} strokeWidth={2} fill="url(#colorValue)" dot={false} activeDot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const s = {
  container: { background: '#fff', borderRadius: '16px', padding: '1.25rem', border: '1px solid rgba(0,0,0,0.06)', marginBottom: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '8px' },
  chartTitle: { fontSize: '14px', fontWeight: '500', color: '#1a1a1a' },
  periodBtns: { display: 'flex', gap: '4px', background: '#f4f1eb', padding: '3px', borderRadius: '8px' },
  periodBtn: { padding: '4px 10px', borderRadius: '6px', border: 'none', background: 'transparent', fontSize: '12px', color: '#6b6b6b', cursor: 'pointer', fontWeight: '500' },
  periodBtnActive: { background: '#fff', color: '#1a1a1a' },
  coinPills: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '0.75rem' },
  pill: { padding: '4px 12px', borderRadius: '99px', border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', fontSize: '12px', color: '#6b6b6b', cursor: 'pointer', fontWeight: '500' },
  pillActive: { background: '#1a6b4a', color: '#fff', borderColor: '#1a6b4a' },
  loading: { height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: '#6b6b6b' },
  empty: { height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: '#6b6b6b' }
};
